// Inbox — landing page, lists every classified inbound reply as an editorial row.
// Filter pills narrow to: All / Pending / Escalated / Awaiting Action.
// Click a row to open ReplyDetail.

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInbox, type InboxRow } from '../lib/queries';
import { IntentBadge } from '../components/IntentBadge';
import { ConfidenceBar } from '../components/ConfidenceBar';
import { EscalationFlag } from '../components/EscalationFlag';
import type { ReplyIntent } from '@stax/shared';

type Filter = 'all' | 'pending' | 'escalated' | 'awaiting';

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'pending', label: 'Pending' },
  { key: 'escalated', label: 'Escalated' },
  { key: 'awaiting', label: 'Awaiting Action' },
];

export function Inbox() {
  const [filter, setFilter] = useState<Filter>('all');
  const inbox = useInbox();

  const rows = inbox.data ?? [];
  const filtered = useMemo(() => filterRows(rows, filter), [rows, filter]);

  return (
    <div className="page">
      <header className="page__head">
        <div className="page__rule">
          <span>Section 01</span>
          <span>Inbox</span>
          <span>
            {new Date().toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </span>
        </div>
        <h1 className="page__title">Inbox</h1>
        <p className="page__deck">
          {inbox.isLoading ? (
            <em>Loading replies…</em>
          ) : inbox.error ? (
            <span className="page__deck--error">
              Couldn’t load inbox: <code>{inbox.error.message}</code>
            </span>
          ) : (
            <>
              <strong>{rows.length}</strong> replies on file ·{' '}
              <strong>{filtered.length}</strong> shown.
            </>
          )}
        </p>
      </header>

      <div className="filter-pills" role="tablist" aria-label="Filter inbox">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={filter === f.key}
            className={`filter-pill${filter === f.key ? ' filter-pill--active' : ''}`}
            onClick={() => setFilter(f.key)}
          >
            <span>{f.label}</span>
            <span className="filter-pill__count">{filterRows(rows, f.key).length}</span>
          </button>
        ))}
      </div>

      {!inbox.isLoading && !inbox.error && filtered.length === 0 && (
        <div className="empty-state">
          <span className="empty-state__rule">No rows</span>
          <p>Nothing matches this filter. Try another one, or wait for the next cron tick.</p>
        </div>
      )}

      <ul className="inbox-list">
        {filtered.map((row) => (
          <li key={row.email_id}>
            <Link to={`/inbox/${row.email_id}`} className="inbox-row">
              <div className="inbox-row__head">
                <div className="inbox-row__from">
                  <strong>{row.from_name ?? row.from_email}</strong>
                  {row.from_name && (
                    <span className="inbox-row__from-email">{row.from_email}</span>
                  )}
                </div>
                <span className="inbox-row__time">{relativeTime(row.received_at)}</span>
              </div>
              <h2 className="inbox-row__subject">{row.subject || '(no subject)'}</h2>
              <p className="inbox-row__preview">{row.body_preview}</p>
              <div className="inbox-row__meta">
                {row.intent ? (
                  <IntentBadge intent={row.intent as ReplyIntent} />
                ) : (
                  <span className="intent-badge intent-badge--unclassified">Unclassified</span>
                )}
                {typeof row.confidence === 'number' && (
                  <ConfidenceBar value={row.confidence} />
                )}
                <EscalationFlag reasons={row.escalation_reasons ?? []} />
                {row.draft_status && (
                  <span className={`draft-status draft-status--${row.draft_status}`}>
                    Draft · {row.draft_status}
                  </span>
                )}
                {row.has_open_escalation && !row.escalation_reasons?.length && (
                  <span className="draft-status draft-status--escalated">Open escalation</span>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

// Filter logic — kept pure so the pill counts can reuse it.
function filterRows(rows: InboxRow[], filter: Filter): InboxRow[] {
  switch (filter) {
    case 'pending':
      return rows.filter((r) => r.draft_status === 'pending');
    case 'escalated':
      return rows.filter((r) => r.has_open_escalation || r.escalation_triggered);
    case 'awaiting':
      // Either there's a draft waiting on us, or it's escalated and unhandled.
      return rows.filter(
        (r) => r.draft_status === 'pending' || r.has_open_escalation,
      );
    case 'all':
    default:
      return rows;
  }
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.round((now - then) / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}
